import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const Pricing = () => {
  return (
    <div>
      <SEO
        title="Engagement Models - Predictable IAM Solutions"
        description="Transparent, scope-based engagements for identity and access management solutions. Predictable investment tailored to your organization's maturity."
        keywords="IAM consulting engagement, identity management pricing, SSO implementation cost, MFA investment, cybersecurity engagement, predictable IAM"
        ogTitle="IDNTY Engagement Models - Predictable IAM Solutions"
        ogDescription="Transparent, scope-based engagements for identity and access management solutions. Predictable investment tailored to your organization's maturity."
        canonicalUrl="https://idnty.one/pricing"
      />
      {/* Hero */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 className="hero-title" style={{ marginBottom: '1rem' }}>
            PREDICTABLE INVESTMENT. CLEAR OUTCOMES.
          </h1>
          <p style={{ fontSize: '1.15rem', maxWidth: '640px', margin: '0 auto 2rem', lineHeight: '1.7' }}>
            We believe in transparent, scope-based engagements. No vague estimates or open-ended hourly billing—just clear, structured models tailored to your organization's maturity.
          </p>
          <Link to="/contact" className="btn">
            Schedule Assessment
          </Link>
        </div>
      </section>

      {/* IAM Tiers */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '3rem' }}>
            IAM TIERS
          </h2>

          <div className="grid-2">
            {/* IAM FOUNDATION */}
            <div className="card-heavy" style={{ background: 'var(--white)' }}>
              <div className="label" style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>IAM FOUNDATION</div>
              <p style={{ fontSize: '1rem', marginBottom: '1.25rem', fontStyle: 'italic', color: 'var(--charcoal)' }}>
                For organizations building structured identity for the first time.
              </p>

              <div style={{ marginBottom: '1.25rem' }}>
                <div className="label" style={{ marginBottom: '0.5rem' }}>Best suited for:</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '1rem' }}>
                  <li>SaaS-first environments</li>
                  <li>Single primary directory</li>
                  <li>Early-stage governance maturity</li>
                  <li>Companies preparing for initial compliance</li>
                </ul>
              </div>

              <div style={{ borderTop: '2px solid var(--black)', padding: '1.25rem 0', marginTop: '1.25rem' }}>
                <div className="label" style={{ marginBottom: '0.75rem' }}>Phase 0 — IAM Readiness Assessment (2–3 Weeks)</div>
                <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: 600 }}>Included prior to implementation.</p>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7' }}>
                  <li>Identity inventory (directory, SaaS, service accounts)</li>
                  <li>Privilege exposure snapshot</li>
                  <li>MFA and authentication posture review</li>
                  <li>Lifecycle process assessment</li>
                  <li>Prioritized 90-day roadmap</li>
                </ul>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic' }}>Outcome: Clear current-state visibility and scoped implementation plan.</p>
              </div>

              <div style={{ borderTop: '2px solid var(--black)', padding: '1.25rem 0' }}>
                <div className="label" style={{ marginBottom: '0.75rem' }}>Implementation (8–10 Weeks Typical)</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7' }}>
                  <li>IAM platform configuration and deployment</li>
                  <li>Single directory integration (e.g., Entra ID, Google, AD)</li>
                  <li>SSO rollout (up to 15 core applications)</li>
                  <li>MFA enforcement across all integrated apps</li>
                  <li>Least-privilege role baseline design</li>
                  <li>Automated joiner/mover/leaver workflows for core systems</li>
                  <li>Conditional access baseline policies</li>
                  <li>Administrator training and operational documentation</li>
                </ul>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic' }}>Outcome: Secure authentication baseline and structured lifecycle control.</p>
              </div>

              <div style={{ borderTop: '2px solid var(--black)', borderBottom: '2px solid var(--black)', padding: '1.25rem 0', marginBottom: '1.25rem' }}>
                <div className="label" style={{ marginBottom: '0.5rem' }}>Managed IAM Governance (Ongoing)</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7' }}>
                  <li>Quarterly access reviews</li>
                  <li>Lifecycle workflow monitoring</li>
                  <li>Policy tuning and optimization</li>
                  <li>Compliance-ready reporting package</li>
                  <li>Email/Slack support (24-hour response)</li>
                  <li>Monthly governance summary report</li>
                </ul>
                <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--gray)' }}>
                  <strong>Included: Trust Monitoring (up to 3 domains).</strong>
                </p>
              </div>

              <div style={{ fontSize: '0.8rem', color: 'var(--gray)', marginTop: '1rem' }}>
                <p><strong>Investment:</strong></p>
                <p>Implementation: Custom quoted based on user count and application scope (Engagements typically start at $10k).</p>
                <p>Managed Governance: Predictable monthly retainer aligned with your workforce scale.</p>
                <p style={{ marginTop: '0.5rem' }}>Platform Licensing: Typically $3–8 per user/month (billed directly by vendor; we assist with negotiation and architecture validation)</p>
              </div>

              <Link to="/contact" className="btn btn-secondary" style={{ width: '100%', textAlign: 'center', marginTop: '1.5rem' }}>
                Schedule Assessment
              </Link>
            </div>

            {/* IAM MATURITY */}
            <div className="card-heavy" style={{ background: 'var(--gold)' }}>
              <div className="label" style={{ marginBottom: '0.5rem', color: 'var(--black)' }}>IAM MATURITY</div>
              <p style={{ fontSize: '1rem', marginBottom: '1.25rem', fontStyle: 'italic', color: 'var(--black)' }}>
                For organizations scaling identity across complex, hybrid, or compliance-driven environments.
              </p>

              <div style={{ marginBottom: '1.25rem' }}>
                <div className="label" style={{ marginBottom: '0.5rem', color: 'var(--black)' }}>Best suited for:</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '1rem', color: 'var(--black)' }}>
                  <li>Multi-directory or hybrid environments</li>
                  <li>SOC 2 / ISO 27001 readiness</li>
                  <li>Growing SaaS footprint</li>
                  <li>Privileged access exposure</li>
                  <li>Early cloud-scale challenges</li>
                </ul>
              </div>

              <div style={{ borderTop: '2px solid var(--black)', padding: '1.25rem 0', marginTop: '1.25rem' }}>
                <div className="label" style={{ marginBottom: '0.75rem', color: 'var(--black)' }}>Phase 0 — Strategic IAM Assessment (3–4 Weeks)</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7', color: 'var(--black)' }}>
                  <li>Enterprise-wide identity discovery (workforce + service accounts)</li>
                  <li>Cloud permission exposure snapshot</li>
                  <li>Privileged account risk review</li>
                  <li>Role model and RBAC gap analysis</li>
                  <li>Compliance alignment assessment (SOC 2 CC6 / ISO A.9 mapping)</li>
                  <li>Executive roadmap with phased execution plan</li>
                </ul>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic', color: 'var(--black)' }}>Outcome: Board-ready identity maturity roadmap with quantified risk areas.</p>
              </div>

              <div style={{ borderTop: '2px solid var(--black)', padding: '1.25rem 0' }}>
                <div className="label" style={{ marginBottom: '0.75rem', color: 'var(--black)' }}>Implementation (12–16 Weeks Typical)</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7', color: 'var(--black)' }}>
                  <li>Includes everything in Foundation, plus:</li>
                  <li>Multiple directory integrations</li>
                  <li>SSO rollout across extended application portfolio (25+ apps)</li>
                  <li>Advanced RBAC model redesign and rationalization</li>
                  <li>Segregation of Duties control design</li>
                  <li>Automated provisioning workflows (HR-driven where applicable)</li>
                  <li>Privileged access policy framework</li>
                  <li>PAM integration design (vaulting, JIT strategy, elevation policy design)</li>
                  <li>Audit-ready documentation and evidence framework</li>
                </ul>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic', color: 'var(--black)' }}>Outcome: Compliance-aligned identity governance with reduced privilege risk.</p>
              </div>

              <div style={{ borderTop: '2px solid var(--black)', borderBottom: '2px solid var(--black)', padding: '1.25rem 0', marginBottom: '1.25rem' }}>
                <div className="label" style={{ marginBottom: '0.5rem', color: 'var(--black)' }}>Managed IAM Governance (Ongoing)</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7', color: 'var(--black)' }}>
                  <li>Monthly access reviews</li>
                  <li>Privileged access oversight reporting</li>
                  <li>Cloud permission posture monitoring</li>
                  <li>Compliance evidence preparation</li>
                  <li>Policy optimization and lifecycle tuning</li>
                  <li>Priority Slack support (4-hour response)</li>
                  <li>Quarterly business review and maturity scoring</li>
                  <li>Audit coordination support</li>
                </ul>
                <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--charcoal)' }}>
                  <strong>Included: Trust Monitoring (up to 10 domains).</strong>
                </p>
              </div>

              <div style={{ fontSize: '0.8rem', color: 'var(--charcoal)', marginTop: '1rem' }}>
                <p><strong>Investment:</strong></p>
                <p>Implementation: Scoped to your specific directory complexity, compliance needs, and existing technical debt.</p>
                <p>Managed Governance: Dedicated monthly retainer with guaranteed SLAs and priority support.</p>
                <p style={{ marginTop: '0.5rem' }}>Platform Licensing: Typically $6–12 per user/month (billed by vendor)</p>
              </div>

              <Link to="/contact" className="btn btn-secondary" style={{ width: '100%', textAlign: 'center', marginTop: '1.5rem' }}>
                Schedule Assessment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Enablement Programs */}
      <section className="section-padding" style={{ background: 'var(--charcoal)', color: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--white)' }}>
            COMPLIANCE ENABLEMENT PROGRAMS
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '1.1rem', maxWidth: '700px', margin: '0 auto 1rem' }}>
            For organizations aligning identity controls with regulatory frameworks.
          </p>
          <p style={{ textAlign: 'center', marginBottom: '3rem', fontSize: '0.95rem', color: 'var(--gray)', maxWidth: '600px', margin: '0 auto 3rem' }}>
            These programs extend IAM implementation into audit-ready compliance posture.
          </p>
          
          <div className="grid-2" style={{ maxWidth: '1000px', margin: '0 auto' }}>
            {/* SOC 2 */}
            <div style={{ border: '2px solid var(--gold)', padding: '2rem', background: 'rgba(255,255,255,0.05)' }}>
              <h3 className="feature-heading" style={{ color: 'var(--gold)', fontSize: '1.25rem', marginBottom: '1rem' }}>SOC 2 Identity & Access Enablement</h3>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '1.1rem', fontWeight: 700 }}>Investment: Tailored proposal based on Phase 0 assessment</p>
                <p style={{ fontSize: '0.9rem', color: 'var(--gold)' }}>Timeline: 3–4 months</p>
              </div>
              <p style={{ fontSize: '0.95rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>Designed for organizations pursuing SOC 2 Type I or Type II readiness.</p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <div className="label" style={{ marginBottom: '0.75rem', fontSize: '0.8rem' }}>Scope:</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7' }}>
                  <li>SOC 2 CC6 access control gap assessment</li>
                  <li>Identity-related control mapping</li>
                  <li>Access governance policy documentation</li>
                  <li>Privileged access control framework</li>
                  <li>Evidence collection workflows</li>
                  <li>Auditor coordination for identity controls</li>
                </ul>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div className="label" style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>Outcome:</div>
                <p style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>Identity governance aligned to SOC 2 requirements with structured evidence readiness.</p>
              </div>

              <div style={{ fontSize: '0.8rem', color: 'var(--gray)', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                <p><strong>Note:</strong> Independent auditor certification performed by licensed CPA firm.</p>
                <p style={{ marginTop: '0.5rem', color: 'var(--gold)' }}>Requires IAM Maturity engagement.</p>
              </div>
            </div>

            {/* Essential Eight */}
            <div style={{ border: '2px solid var(--gold)', padding: '2rem', background: 'rgba(255,255,255,0.05)' }}>
              <h3 className="feature-heading" style={{ color: 'var(--gold)', fontSize: '1.25rem', marginBottom: '1rem' }}>Essential Eight – Identity Control Uplift</h3>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '1.1rem', fontWeight: 700 }}>Investment: Tailored proposal based on Phase 0 assessment</p>
                <p style={{ fontSize: '0.9rem', color: 'var(--gold)' }}>Timeline: 2–4 months</p>
              </div>
              <p style={{ fontSize: '0.95rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>Aligned to the Australian Cyber Security Centre Essential Eight framework.</p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <div className="label" style={{ marginBottom: '0.75rem', fontSize: '0.8rem' }}>Scope:</div>
                <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', lineHeight: '1.7' }}>
                  <li>Essential Eight maturity assessment (identity-related controls)</li>
                  <li>MFA uplift strategy</li>
                  <li>Administrative privilege restriction model</li>
                  <li>IAM policy documentation</li>
                  <li>Roadmap to higher maturity tier</li>
                </ul>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div className="label" style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>Outcome:</div>
                <p style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>Improved Essential Eight maturity posture across identity and access controls.</p>
              </div>

              <div style={{ fontSize: '0.8rem', color: 'var(--gray)', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                <p><strong>Note:</strong> Covers identity-focused controls. Broader security uplift may require additional cybersecurity services.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform licensing reference */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
            PLATFORM PRICING REFERENCE
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--gray)', fontSize: '0.9rem', maxWidth: '640px', margin: '0 auto 2rem' }}>
            We're vendor-agnostic and help you choose based on your stack and budget. Platform costs are billed separately — you pay the vendor directly. We're licensed partners and negotiate volume discounts on your behalf.
          </p>
          <p style={{ textAlign: 'center', fontSize: '0.95rem', color: 'var(--charcoal)', maxWidth: '720px', margin: '0 auto' }}>
            IAM solution licensing typically runs $3–18/user/month depending on vendor and tier; we help you choose and negotiate volume discounts.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--black)', color: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="section-title" style={{ color: 'var(--white)', marginBottom: '1rem' }}>
            READY TO GET STARTED?
          </h2>
          <div style={{ marginBottom: '2rem', fontSize: '1.1rem' }}>
            <p>Join organizations building secure, scalable, and compliant identity infrastructure.</p>
          </div>
          <Link to="/contact" className="btn btn-gold">
            Schedule Assessment
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
